import React, { useRef, useState } from "react";
import SummarizeIcon from "@mui/icons-material/Summarize";
import RoomIcon from "@mui/icons-material/Room";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import { Avatar, Modal, Typography, Box, Grid } from "@mui/material";
import { English } from "../../../constant/EnglishFallback";
import { useSelector } from "react-redux";

function ImageChooseFromModal({
  open,
  onClose,
  setAddPictureModalOpen,
  setPictureList,
  pictureList,
  addIconPosition,
  selectedFromEmpty,
  imageCount,
  setImageCount,
  setSnackFlag,
  // NEW PROPS for video support
  setVideoTrimmerOpen,
  setSelectedVideoFile,
  totalDuration = 0
}) {
  const { multiLanguageData } = useSelector((state) => state?.main);
  const LanguageData = multiLanguageData?.Montages || {};
  const {
    TEXT_ADD_BLANK_PAGE = English.Montages.TEXT_ADD_BLANK_PAGE,
    TEXT_PHOTOS_FROM_PROJECT = English.Montages.TEXT_PHOTOS_FROM_PROJECT,
    TEXT_ADD_FROM_COMPUTER = English.Montages.TEXT_ADD_FROM_COMPUTER,
    TEXT_SNACKBAR_IMAGE_LIMIT = English.Montages.TEXT_SNACKBAR_IMAGE_LIMIT,
    TEXT_ADD_VIDEO = "Add Video",
    TEXT_VIDEO_LIMIT = "Maximum 2 videos allowed",
    TEXT_DURATION_LIMIT = "Total montage duration cannot exceed 60 seconds"
  } = LanguageData || {};

  const fileRef = useRef(null);
  const videoRef = useRef(null);

  // Calculate current counts
  const currentImageCount = pictureList?.filter((item) => item?.imageFrom !== "Video")?.length ?? 0;

  const currentVideoCount = pictureList?.filter((item) => item?.imageFrom === "Video")?.length ?? 0;

  function handleBlankPageAdd() {
    const picArray = [...pictureList];
    const newImage = {
      id: Date.now(),
      fileUrl:
        "https://biappstoragetest2.blob.core.windows.net/userprofiles/96/20230427231517132img.jpg",
      mimeType: "image/jpeg",
      name: "image" + (picArray.length + 1).toString(),
      imageFrom: "Blank",
      imageDescription: "",
      duration: 5 // Each image is 5 seconds
    };
    picArray.splice(selectedFromEmpty ? pictureList.length : addIconPosition + 1, 0, newImage);
    setPictureList(picArray);
    onClose();
    setSnackFlag(true);
  }

  function handleGalleryAdd() {
    // Check if adding more images would exceed the 5 image limit
    if (currentImageCount >= 5) {
      alert(TEXT_SNACKBAR_IMAGE_LIMIT);
      return;
    }

    // Check if adding an image would exceed 60 second total duration
    if (totalDuration + 5 > 60) {
      alert(`${TEXT_DURATION_LIMIT}. Current duration: ${totalDuration.toFixed(1)}s`);
      return;
    }

    fileRef.current.click();
  }

  const handleChange = (event) => {
    // Validate all files are images
    let allFilesValid = true;
    if (fileRef?.current?.files?.length > 0) {
      for (let i = 0; i < fileRef.current.files.length; i++) {
        if (!fileRef.current.files[i].type.startsWith("image/")) {
          allFilesValid = false;
          break;
        }
      }
    }
    if (!allFilesValid) {
      return alert("Please select valid image files (JPEG, PNG, GIF, etc.).");
    }

    const selectedFileLength = fileRef.current?.files?.length ?? 0;

    // Check if total images would exceed 5
    if (currentImageCount + selectedFileLength > 5) {
      return alert(
        `You can only add ${5 - currentImageCount} more image(s). (Max 5 images per montage)`
      );
    }

    // Check if adding these images would exceed 60 second duration
    const additionalDuration = selectedFileLength * 5;
    if (totalDuration + additionalDuration > 60) {
      const maxImagesCanAdd = Math.floor((60 - totalDuration) / 5);
      return alert(
        `Adding ${selectedFileLength} image(s) would exceed 60 second limit. You can add maximum ${maxImagesCanAdd} more image(s).`
      );
    }

    const newImages = Array.from(fileRef.current.files);
    setImageCount(currentImageCount + newImages.length);
    const updatedPictureList = [...pictureList];

    newImages.forEach((file, index) => {
      const imageObj = {
        id: Date.now() + index,
        fileUrl: URL.createObjectURL(file),
        galleryUploadUrl: file,
        type: file.type,
        name: "image" + (updatedPictureList.length + 1 + index).toString(),
        imageFrom: "Gallery",
        imageDescription: "",
        duration: 5 // Each image is 5 seconds
      };
      updatedPictureList.splice(
        selectedFromEmpty ? updatedPictureList.length : addIconPosition + 1 + index,
        0,
        imageObj
      );
    });

    setPictureList(updatedPictureList);
    fileRef.current.value = "";
    onClose();
    setSnackFlag(true);
  };

  // NEW: Handle video selection
  const handleVideoAdd = () => {
    // Check video count limit
    if (currentVideoCount >= 2) {
      alert(TEXT_VIDEO_LIMIT);
      return;
    }

    // Check if adding minimum video duration would exceed limit
    if (totalDuration >= 60) {
      alert(`${TEXT_DURATION_LIMIT}. Current duration: ${totalDuration.toFixed(1)}s`);
      return;
    }

    videoRef.current.click();
  };

  // NEW: Handle video file selection
  const handleVideoChange = (event) => {
    const file = videoRef.current?.files?.[0];

    if (!file) return;

    // Validate it's a video file
    if (!file.type.startsWith("video/")) {
      alert("Please select a valid video file (MP4, WebM, OGG)");
      return;
    }

    // Check file size (optional - e.g., 100MB limit)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      alert("Video file size must be less than 100MB");
      return;
    }

    // Get video duration to validate
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      const videoDuration = video.duration;
      URL.revokeObjectURL(video.src);

      // Check if video duration exceeds limits
      if (videoDuration > 60) {
        alert("Video duration cannot exceed 60 seconds. Please trim the video.");
      }

      // Check if adding this video would exceed total duration
      const potentialDuration = totalDuration + Math.min(videoDuration, 60);
      if (potentialDuration > 60) {
        alert(
          `Adding this video would exceed 60 second limit.\nCurrent duration: ${totalDuration.toFixed(1)}s\nVideo duration: ${videoDuration.toFixed(1)}s\nRemaining: ${(60 - totalDuration).toFixed(1)}s`
        );
        return;
      }

      // Open video trimmer modal
      setSelectedVideoFile(file);
      setVideoTrimmerOpen(true);
      onClose();
    };

    video.onerror = () => {
      alert("Error loading video file. Please try another file.");
      URL.revokeObjectURL(video.src);
    };

    video.src = URL.createObjectURL(file);
    videoRef.current.value = "";
  };

  const openForSite = () => {
    onClose();
    setAddPictureModalOpen(true);
  };

  const cardOptions = [
    {
      icon: <SummarizeIcon />,
      text: TEXT_ADD_BLANK_PAGE,
      onClick: handleBlankPageAdd,
      avatarSx: {
        backgroundColor: "white",
        border: "3px solid grey",
        color: "black",
        cursor: "pointer"
      },
      disabled: totalDuration + 5 > 60
    },
    {
      icon: <RoomIcon />,
      text: TEXT_PHOTOS_FROM_PROJECT,
      onClick: openForSite,
      avatarSx: {
        backgroundColor: "white",
        border: "3px solid red",
        color: "black",
        cursor: "pointer"
      },
      disabled: currentImageCount >= 5 || totalDuration + 5 > 60
    },
    {
      icon: <Typography variant="subtitle1">{`${currentImageCount}/5`}</Typography>,
      text: TEXT_ADD_FROM_COMPUTER,
      onClick: handleGalleryAdd,
      avatarSx: {
        backgroundColor: "white",
        border: "3px solid yellow",
        color: "black",
        cursor: currentImageCount >= 5 ? "not-allowed" : "pointer",
        opacity: currentImageCount >= 5 ? 0.5 : 1
      },
      disabled: currentImageCount >= 5 || totalDuration + 5 > 60
    },
    {
      icon: (
        <VideoLibraryIcon
          sx={{
            color: currentVideoCount >= 2 ? "grey" : "black"
          }}
        />
      ),
      text: (
        <Box textAlign="center">
          <Typography variant="subtitle1">{TEXT_ADD_VIDEO}</Typography>
          <Typography variant="caption" color="textSecondary">
            {currentVideoCount}/2
          </Typography>
        </Box>
      ),
      onClick: handleVideoAdd,
      avatarSx: {
        backgroundColor: "white",
        border: "3px solid purple",
        color: "black",
        cursor: currentVideoCount >= 2 ? "not-allowed" : "pointer",
        opacity: currentVideoCount >= 2 || totalDuration >= 60 ? 0.5 : 1
      },
      disabled: currentVideoCount >= 2 || totalDuration >= 60
    }
  ];

  return (
    <>
      {/* Image file input */}
      <input
        type="file"
        id="fileLen"
        style={{ display: "none" }}
        multiple
        ref={fileRef}
        accept="image/*"
        onChange={handleChange}
      />

      {/* Video file input */}
      <input
        type="file"
        id="videoFile"
        style={{ display: "none" }}
        ref={videoRef}
        accept="video/mp4,video/webm,video/ogg"
        onChange={handleVideoChange}
      />

      <Modal open={open} onClose={onClose}>
        <Box className="user-modal-responsive">
          {/* Duration info */}

          <Grid container spacing={2} columns={{ xs: 12, md: 12 }}>
            {cardOptions.map((option, index) => (
              <Grid key={index} size={{ xs: 6, sm: 6, md: 3 }}>
                <Box
                  onClick={option.disabled ? null : option.onClick}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    opacity: option.disabled ? 0.5 : 1,
                    pointerEvents: option.disabled ? "none" : "auto"
                  }}>
                  <Avatar sx={option.avatarSx}>{option.icon}</Avatar>
                  <Typography
                    variant="subtitle2"
                    textAlign="center"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                    {option.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Modal>
    </>
  );
}

export default ImageChooseFromModal;
